import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Rss,
  BarChart3,
  User,
  X,
  LogOut,
  Settings,
  FileText,
  LogIn,
  Trophy,
  Lock,
  HelpCircle
} from 'lucide-react';
import { isValidImageUrl } from '../utils/avatarUtils';
import { isCreator } from '../services/auth';

interface MobileNavigationProps {
  user: any;
  onLogout: () => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ user, onLogout }) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu when navigating to a new page
  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isProfileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isProfileMenuOpen]);

  // Left side nav items (before center Shop button)
  const leftNavItems = user
    ? [
        { name: 'Picks', path: '/my-picks', icon: Rss },
        { name: isCreator() ? 'Dashboard' : 'Creator', path: '/dashboard', icon: BarChart3 }
      ]
    : [
        { name: 'Picks', path: '/auth', icon: Rss, locked: true },
        { name: 'Creator', path: '/auth', icon: BarChart3, locked: true }
      ];

  // Right side nav items (after center Shop button)
  const rightNavItems: any[] = [];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getProfilePicture = () => {
    if (user?.profile_picture && isValidImageUrl(user.profile_picture)) {
      return (
        <img
          src={user.profile_picture}
          alt={user.name || 'Profile'}
          className="w-full h-full object-cover"
        />
      );
    }

    if (user?.profile_picture) {
      return (
        <span className="text-xl">
          {user.profile_picture}
        </span>
      );
    }

    if (user) {
      return (
        <span className="text-xs font-bold">
          {getInitials(user.name || user.email || 'U')}
        </span>
      );
    }

    return <User className="w-5 h-5" />;
  };

  return (
    <>
      {/* Profile Menu Modal */}
      <AnimatePresence>
        {isProfileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsProfileMenuOpen(false)}
              className="md:hidden fixed inset-0 bg-black/50 z-40"
            />

            {/* Bottom Sheet Style Menu */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="md:hidden fixed bottom-0 left-0 right-0 bg-dark-900/98 backdrop-blur-md border-t border-neon-green/20 z-50 rounded-t-2xl max-h-[80vh] overflow-y-auto"
            >
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1 bg-gray-600 rounded-full" />
              </div>

              {/* Close Button */}
              <div className="flex justify-end px-4 pb-2">
                <button
                  onClick={() => setIsProfileMenuOpen(false)}
                  className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Profile Section */}
              <div className="px-6 pb-4 border-b border-neon-green/20">
                {user ? (
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-green to-neon-blue flex items-center justify-center text-white overflow-hidden">
                      {getProfilePicture()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium text-lg truncate">
                        {user.name || 'User'}
                      </div>
                      <div className="text-gray-400 text-sm truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-dark-700 flex items-center justify-center text-gray-400">
                      <User className="w-8 h-8" />
                    </div>
                    <div>
                      <div className="text-gray-400 text-lg">
                        Not signed in
                      </div>
                      <Link
                        to="/auth"
                        className="text-neon-green text-sm hover:underline"
                      >
                        Sign in to continue
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Menu Items */}
              <div className="p-4">
                {user ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        onLogout();
                        setIsProfileMenuOpen(false);
                      }}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium text-gray-300 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 w-full text-left"
                    >
                      <LogOut className="w-5 h-5 flex-shrink-0" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-neon-green text-dark-900 rounded-lg font-medium hover:bg-neon-green/90 transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    <span>Sign In</span>
                  </Link>
                )}

                {/* Legal Links Section */}
                <div className="mt-6 pt-4 border-t border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3 px-2">
                    Legal
                  </div>
                  <div className="space-y-1">
                    <Link
                      to="/privacy-policy"
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-neon-green hover:bg-neon-green/5 transition-all duration-200"
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span>Privacy Policy</span>
                    </Link>
                    <Link
                      to="/terms-of-service"
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-neon-green hover:bg-neon-green/5 transition-all duration-200"
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span>Terms of Service</span>
                    </Link>
                    <Link
                      to="/support"
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-neon-green hover:bg-neon-green/5 transition-all duration-200"
                    >
                      <HelpCircle className="w-4 h-4 flex-shrink-0" />
                      <span>Support</span>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation for Mobile */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-dark-900/95 backdrop-blur-md border-t border-neon-green/20">
        <div className="flex items-center justify-around px-2 pb-2 pt-1">
          {/* Left Navigation Items */}
          {leftNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isLocked = (item as any).locked || false;

            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex flex-col items-center py-2 px-2 rounded-lg transition-all duration-200 min-w-[60px] justify-center ${
                  isLocked
                    ? 'text-gray-500'
                    : isActive
                    ? 'text-neon-green'
                    : 'text-gray-400 hover:text-neon-green'
                }`}
              >
                <div className="relative">
                  <Icon className="w-5 h-5" />
                  {isLocked && (
                    <Lock className="w-3 h-3 absolute -top-1 -right-1 text-gray-500" />
                  )}
                </div>
                <span className="text-[10px] mt-1 font-medium">
                  {item.name}
                </span>
              </Link>
            );
          })}

          {/* Shop Button */}
          <Link
            to="/shop-picks"
            className={`flex flex-col items-center py-2 px-2 rounded-lg transition-all duration-200 min-w-[60px] justify-center ${
              location.pathname === '/shop-picks'
                ? 'text-neon-green'
                : 'text-gray-400 hover:text-neon-green'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            <span className="text-[10px] mt-1 font-medium">
              Shop
            </span>
          </Link>

          {/* Profile Button - with clearer sign-in status */}
          <button
            onClick={() => setIsProfileMenuOpen(true)}
            className={`flex flex-col items-center py-2 px-2 rounded-lg transition-all duration-200 min-w-[60px] justify-center ${
              isProfileMenuOpen
                ? 'text-neon-green'
                : user
                ? 'text-gray-400 hover:text-neon-green'
                : 'text-gray-500 hover:text-gray-400'
            }`}
          >
            <div className={`relative w-6 h-6 rounded-full flex items-center justify-center overflow-hidden ${
              user
                ? 'bg-gradient-to-br from-neon-green to-neon-blue text-white'
                : 'bg-dark-700 border-2 border-gray-600 text-gray-400'
            }`}>
              {user?.profile_picture && isValidImageUrl(user.profile_picture) ? (
                <img
                  src={user.profile_picture}
                  alt={user.name || 'Profile'}
                  className="w-full h-full object-cover"
                />
              ) : user?.profile_picture ? (
                <span className="text-sm">
                  {user.profile_picture}
                </span>
              ) : user ? (
                <span className="text-[8px] font-bold">
                  {getInitials(user.name || user.email || 'U')}
                </span>
              ) : (
                <User className="w-4 h-4" />
              )}
              {!user && (
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full border border-dark-900"></div>
              )}
            </div>
            <span className="text-[10px] mt-1 font-medium">
              {user ? 'Profile' : 'Sign In'}
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileNavigation;